import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Read-through listing of Zocdoc's own appointments — an ops explorer, not part of the ingest
 * path. Nothing is stored: importing a booking is what makes it durable, and this screen
 * exists so the payload Zocdoc actually sends can be read off a live response before that
 * pipeline is written against it (the partner sandbox returns an empty list).
 *
 * Every mapped field is optional because the appointment payload's shape is unconfirmed;
 * `raw` is the record exactly as Zocdoc sent it, which is the point of the screen.
 */
const appointmentSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  startAt: z.string().optional(),
  endAt: z.string().optional(),
  schedulableEntityId: z.string().optional(),
  visitReasonId: z.string().optional(),
  lastModifiedAt: z.string().optional(),
  patient: z
    .object({
      id: z.string().optional(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      dateOfBirth: z.string().optional(),
      gender: z.string().optional(),
      phone: z.string().optional(),
      email: z.string().optional(),
    })
    .optional(),
  /**
   * The IANA zone of the schedulable entity this appointment belongs to, from one
   * `GET /v1/schedulable_entities` read per request — **not** one per appointment.
   *
   * `startAt` already carries a UTC offset, but an offset is not a zone: `-04:00` cannot tell you
   * whether the practice is in New York or Detroit, and it changes across a DST boundary. Absent
   * when the roster does not describe the entity.
   */
  timeZone: z.string().optional(),
  /**
   * Who the visit is with — keyed on Zocdoc's `provider_id`, which is read from the participants
   * call when it ran and otherwise split out of `schedulableEntityId`.
   *
   * Two names, because they are two different facts and an operator needs to tell them apart:
   * `practitionerName` is the beHuman doctor the booking will actually land on, and `zocdocName`
   * is what Zocdoc's own directory calls the provider. A row with the second and not the first is
   * an **unmapped** provider — a booking with nowhere to go, which is exactly the thing worth
   * spotting on this screen.
   *
   * **A live view, not history.** The practitioner is whoever the provider link names *now*; if a
   * provider is relinked, past appointments re-render with the new doctor. Nothing here is an
   * audit trail of who the booking was assigned to at the time.
   *
   * Absent when the appointment names no entity and participants were not read — there is then no
   * provider to resolve.
   */
  provider: z
    .object({
      zocdocProviderId: z.string().optional(),
      /** Zocdoc's own name for the provider, from `GET /v1/providers?npis=…`. */
      zocdocName: z.string().optional(),
      specialtyName: z.string().optional(),
      npi: z.string().optional(),
      /**
       * The mapped beHuman practitioner — the chart carrying this `pr_…` as an identifier.
       *
       * Absent means no practitioner claims the provider, **or** that the search could not be
       * run; the two are told apart by `providerError`, not by this field. They used to be
       * distinguishable here, back when the mapping and the name came from two different reads.
       */
      practitionerId: z.string().optional(),
      /** The same chart's name, from the same search — so it is present whenever the id is. */
      practitionerName: z.string().optional(),
    })
    .optional(),
  /**
   * Whether this Zocdoc booking already exists on the mapped practitioner's beHuman calendar.
   *
   * Only resolvable once `provider.practitionerId` is known — there is no calendar to look at
   * before a provider is linked — so it is **absent**, not a state, for an unmapped provider.
   *
   * Matched on the **start instant** alone. Zocdoc's `startAt` carries a UTC offset and beHuman's
   * `start` is a UTC `Z`, so the two are compared as instants; no tolerance window is applied,
   * because a near-match would invent both false "already synced" labels and false conflicts.
   *
   * An inference, not a recorded fact: nothing yet writes down that these two are the same visit,
   * so this is re-derived on every read.
   */
  calendar: z
    .object({
      state: z.enum([
        /**
         * Bound: a beHuman `Appointment` carries this Zocdoc appointment's id.
         *
         * The only state that is a **fact** rather than an inference, and the difference matters
         * — it is the state a Zocdoc cancellation can safely act on, because the pairing was
         * checked and written rather than guessed from a matching start time.
         */
        'synced',
        /**
         * A beHuman `Appointment` for that practitioner starts at exactly this time.
         *
         * Keyed on the Appointment and never on an Encounter alone: an ad-hoc, appointment-less
         * encounter's start snaps to the same half-hour grid as a real bookable slot
         * (`appointment/appointment.service.ts:592`), so a walk-in would otherwise read as a
         * booking.
         */
        'booked',
        /** A `free` Slot at this time — the visit could be booked into it. */
        'slot-free',
        /**
         * A `busy` Slot with no appointment on it. Neither bookable nor booked: a claimed slot
         * whose booking is gone, which is worth seeing rather than smoothing over.
         */
        'slot-claimed',
        /** No Slot at all at this time — the practitioner published no availability here. */
        'no-slot',
      ]),
      appointmentId: z.string().optional(),
      encounterId: z.string().optional(),
      slotId: z.string().optional(),
      /**
       * Whether the beHuman booking is for *this* Zocdoc patient — the difference between "already
       * synced" and "somebody else holds that time".
       *
       * Absent when it cannot be decided, which is the common case before a chart is confirmed:
       * `associatedPatients` is an email-and-birth-date guess, and guessing here would label a
       * stranger's visit as this one. The other patient is deliberately **not** named — whether it
       * differs is all the screen needs.
       */
      samePatient: z.boolean().optional(),
    })
    .optional(),
  /**
   * The PHI-bearing half, from `GET /v1/appointments/{id}/participants`. Absent when
   * `includeParticipants` was false, or when that read failed — `participantsError` says which.
   */
  participants: z
    .object({
      providerId: z.string().optional(),
      locationId: z.string().optional(),
      patient: z
        .object({
          patientId: z.string().optional(),
          developerPatientId: z.string().optional(),
          firstName: z.string().optional(),
          lastName: z.string().optional(),
          dateOfBirth: z.string().optional(),
          sexAtBirth: z.string().optional(),
          genderIdentity: z.array(z.string()).optional(),
          phone: z.string().optional(),
          email: z.string().optional(),
          address1: z.string().optional(),
          address2: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          zipCode: z.string().optional(),
          /** Someone booked on this patient's behalf — a proxy booking, not a data problem. */
          isBookedByAnotherPatient: z.boolean().optional(),
          insurance: z
            .object({
              planId: z.string().optional(),
              memberId: z.string().optional(),
              groupNumber: z.string().optional(),
              isSelfPay: z.boolean().optional(),
            })
            .optional(),
        })
        .optional(),
      /** Names only — nothing is downloaded. */
      documentNames: z.array(z.string()).optional(),
      requestId: z.string().optional(),
      /** The participants record exactly as Zocdoc sent it. PHI. */
      raw: z.record(z.string(), z.unknown()),
    })
    .optional(),
  /**
   * Why this row has no participants. Per-row rather than fatal: one 403 or 404 must not blank the
   * whole list, and "we could not read it" is a different answer from "there is no patient".
   */
  participantsError: z.string().optional(),
  /**
   * beHuman charts that look like this appointment's patient, found by **email plus date of
   * birth** against FHIR.
   *
   * Candidates, **not a match decision.** Real patient matching is a later deliverable and needs a
   * review queue; this is the cheap lookup that tells an operator "we may already hold this
   * person" so a booking is not filed against a duplicate chart. Nothing downstream reads it.
   *
   * Empty means one of three things, and the screen should not pretend otherwise: no chart
   * matched, participants were not read (so there was no email to search on), or the patient has
   * no email. `associatedPatientsError` covers the fourth — the search itself failed.
   */
  associatedPatients: z.array(
    z.object({
      patientId: z.string(),
      firstName: z.string().optional(),
      /** `Patient.birthDate`, `YYYY-MM-DD`. */
      dob: z.string().optional(),
      email: z.string().optional(),
      phoneNumber: z.string().optional(),
      /**
       * Every Zocdoc `patient_id` already on this chart's `Patient.identifier`
       * (`https://behuman.health/zocdoc-patient`). Empty when the chart is bound to none.
       *
       * **A list, because one person is not one Zocdoc patient**: Zocdoc mints a `patient_id` per
       * patient record, so the same human booking as a guest, from another device or under another
       * email arrives as several ids that all resolve to this one chart.
       *
       * Non-empty means the chart is bound to *some* Zocdoc patient — **check that it contains
       * this appointment's own patient id before calling it synced**, because a chart bound only
       * to other Zocdoc patients is not a match for this one.
       */
      zocdocPatientIds: z.array(z.string()),
    }),
  ),
  /** Set when the FHIR lookup failed. Distinct from an empty result, which means "none matched". */
  associatedPatientsError: z.string().optional(),
  /** PHI. Rendered for an admin, never logged — the client logs only the redacted summary. */
  raw: z.record(z.string(), z.unknown()),
})

/**
 * A POST because it is an action, not a resource read: it reaches out to Zocdoc on demand and
 * is rate-limited by them. Nothing beHuman-side is touched — the payload in and out is Zocdoc's
 * own data only.
 *
 * **The paging is Zocdoc's, deliberately, and not the repository's `page: { offset, count, limit }`
 * shape.** That convention governs our own lists, where an offset is free. This is a read-through
 * of `GET /v1/appointments`, whose paging is a zero-indexed page index capped at 10 — an offset
 * would have to be divided back into one, and a `limit` would mean nothing upstream. Presenting a
 * freedom the vendor does not have is the worse of the two mismatches.
 */
export const zocdocFetchAppointments = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments/fetch' })
  .input(
    z.object({
      /**
       * **Zero-indexed**, matching Zocdoc: page 0 is the first page, and 10 is its maximum.
       * `.positive()` here was a bug — it made the first page unrequestable.
       */
      page: z.number().int().min(0).max(10).optional(),
      pageSize: z.number().int().positive().max(100).optional(),
      /**
       * Inclusive `YYYY-MM-DD` window on the appointment's **start**, mapped to Zocdoc's
       * `start_time_utc_min` / `_max` (both verified live).
       *
       * The window is applied in **UTC**, while an appointment's `start_time` carries the
       * provider's own offset — so a visit late on `toDay` in a western zone can fall outside a
       * window built from local calendar days. Deliberately not corrected here: the correction
       * needs a zone, an appointment can belong to a provider in any zone, and quietly widening
       * the window would make the count disagree with the filter the caller asked for.
       */
      fromDay: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
      toDay: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
      /**
       * Also read each appointment's participants. **Defaults to true**, because an appointment
       * without its patient is not much of an appointment on this screen.
       *
       * It costs **one Zocdoc request per appointment** on top of the list read (the client's own
       * limiter caps them at four in flight), so pass `false` for a caller that only needs times
       * and counts. Zones cost nothing extra either way — they come from a single roster read.
       */
      includeParticipants: z.boolean().optional(),
    }),
  )
  .output(
    z.object({
      ok: z.boolean(),
      page: z.number().optional(),
      pageSize: z.number().optional(),
      totalCount: z.number().optional(),
      hasMore: z.boolean().optional(),
      appointments: z.array(appointmentSchema).optional(),
      /** The UTC instants the window was actually sent as, so the screen can say what it asked. */
      startTimeUtcMin: z.string().optional(),
      startTimeUtcMax: z.string().optional(),
      /**
       * How many rows came back without participants because that read failed. Surfaced as a count
       * so a screen can say "3 of 12 could not be read" instead of quietly showing 9.
       */
      participantsFailed: z.number().optional(),
      /**
       * Why the practitioner column is thin, when it is. Said out loud rather than left as a gap:
       * a provider whose name could not be read looks exactly like an unmapped one in the table,
       * and they mean different things.
       */
      providerError: z.string().optional(),
      /**
       * Why the calendar column is blank, when it is. "Could not check" and "not on the calendar"
       * are different answers, and only one of them is a reason to go looking.
       */
      calendarError: z.string().optional(),
      missingEnv: z.array(z.string()).optional(),
      status: z.number().optional(),
      requestId: z.string().optional(),
      error: z.string().optional(),
    }),
  )
